/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// schematic-view.js — the derived LOGICAL view (Feature 150): the same
// DeskDoc, drawn as chip symbols + routed named nets + fat bus lines rather
// than a physical breadboard. It is a PROJECTION — the desk stays the single
// source of truth; only per-symbol position nudges are stored (schematicPos).
//
// The surface reuses the desk camera (DeskView) so pan/zoom/grid behave
// identically and stay transform-only. The picture rebuilds only on a document
// change (a fresh `layout()`), never on a camera move. Live simulation tint and
// the shared probe highlight are applied IN PLACE from `chiphippo:sim-state` /
// `chiphippo:net-probed` by flipping `data-level` / a highlight class on the
// already-drawn elements — the schematic never queries the engine.
//
// DOM building lives in the pure exported functions (jsdom-testable); the class
// wires them to the document, the camera, and symbol-drag.

import { el, svgEl } from "../dom.js";
import { t } from "../i18n.js";
import { PX_PER_UNIT, clampZoom } from "../desk/desk-geometry.js";
import { layout } from "../model/schematic-layout.js";
import { formatOhms } from "../model/ohm-format.js";
import { formatFarads } from "../model/farad-format.js";
import { formatHenries } from "../model/henry-format.js";
import { formatVolts } from "../model/volt-format.js";
import { partNumberOf } from "../catalog/discretes.js";
import { loadBadge } from "../catalog/bench-parts.js";
import {
  acBadge,
  adapterBadge,
  transformerBadge,
} from "../catalog/ac-parts.js";
import { speakerParams } from "../catalog/speaker.js";
import { FLAG_LEN, flagPolygon } from "../model/signals.js";
import { DeskView } from "./desk-view.js";
import { beginPointerGesture, releaseWorld } from "./pointer-gesture.js";
import { NetlistCache } from "./netlist-cache.js";
import { ZoomControl } from "./zoom-control.js";

function svgText(text, x, y, cls, size, anchor = "middle") {
  const t = svgEl("text", {
    class: cls,
    x,
    y,
    "font-size": size,
    "text-anchor": anchor,
    "dominant-baseline": "middle",
  });
  t.textContent = text;
  return t;
}

const PAD = 4; // world-unit margin around the diagram bounds

// ── Distinctive-shape gate glyphs (a FIXED vocabulary, never per-chip) ────────

/** A small distinctive-shape badge, centred at the TOP of the box (above the
    pin rows) so it never collides with a pin or the part-number label. */
function buildGlyph(name, geometry) {
  const s = 1.5;
  const g = svgEl("g", {
    class: "schematic-glyph",
    transform: `translate(${geometry.width / 2 - s * 0.6} 0.5) scale(${s})`,
  });
  const inverting =
    name === "NAND" || name === "NOR" || name === "XNOR" || name === "NOT";
  const base =
    name === "NAND"
      ? "AND"
      : name === "NOR"
        ? "OR"
        : name === "XNOR"
          ? "XOR"
          : name === "NOT"
            ? "BUFFER"
            : name;
  let bodyD;
  if (base === "AND") {
    bodyD = "M0,0 L0.45,0 A0.5,0.5 0 0 1 0.45,1 L0,1 Z";
  } else if (base === "OR" || base === "XOR") {
    bodyD = "M0.02,0 Q0.5,0.12 0.95,0.5 Q0.5,0.88 0.02,1 Q0.34,0.5 0.02,0 Z";
  } else {
    bodyD = "M0,0 L0.9,0.5 L0,1 Z"; // BUFFER triangle
  }
  g.append(svgEl("path", { class: "schematic-glyph-shape", d: bodyD }));
  if (base === "XOR") {
    g.append(
      svgEl("path", {
        class: "schematic-glyph-line",
        d: "M-0.14,0 Q0.18,0.5 -0.14,1",
      }),
    );
  }
  if (inverting) {
    g.append(
      svgEl("circle", {
        class: "schematic-glyph-shape",
        cx: (base === "BUFFER" ? 0.9 : 0.98) + 0.09,
        cy: 0.5,
        r: 0.09,
      }),
    );
  }
  return g;
}

// ── External-signal stubs (Feature 370) ──────────────────────────────────────

/**
 * A signal's flag at a pin tip, oriented outward along its side, plus its name.
 *
 * The pentagon comes from `flagPolygon` — the SAME function the desk layer
 * draws with — because the desk and the schematic must not show two different
 * flags for one signal. Here the apex points AT the pin, so the body extends
 * outward: at rot 0 the body runs right of its point, so a port on the LEFT
 * side wants rot 180, and so on round.
 */
function buildSignalSymbol({ x, y, side, name, color }) {
  const dir =
    side === "left"
      ? { x: -1, y: 0 }
      : side === "right"
        ? { x: 1, y: 0 }
        : side === "top"
          ? { x: 0, y: -1 }
          : { x: 0, y: 1 };
  const rot =
    side === "left" ? 180 : side === "right" ? 0 : side === "top" ? 270 : 90;
  const g = svgEl("g", { class: "schematic-signal" });
  g.style.setProperty("--signal-color", `var(--color-wire-${color})`);
  const tip = { x: x + dir.x * 0.9, y: y + dir.y * 0.9 };
  g.append(
    svgEl("line", {
      class: "schematic-signal-stem",
      x1: x,
      y1: y,
      x2: tip.x,
      y2: tip.y,
    }),
    svgEl("polygon", {
      class: "schematic-signal-flag",
      points: flagPolygon(tip, rot)
        .map((v) => `${v.x},${v.y}`)
        .join(" "),
    }),
  );
  // The label sits past the body, on the axis the flag points along. `svgText`
  // writes font-size as an ATTRIBUTE (SVG user units), so the type-scale guard
  // has nothing to say about it.
  const label = {
    x: tip.x + dir.x * (FLAG_LEN + 0.4),
    y: tip.y + dir.y * (FLAG_LEN + 0.4),
  };
  g.append(
    svgText(
      name,
      label.x,
      label.y,
      "schematic-signal-label",
      0.85,
      side === "left" ? "end" : side === "right" ? "start" : "middle",
    ),
  );
  return g;
}

// ── Power / ground rail symbols ───────────────────────────────────────────────

/** A VCC bar / GND ladder at a pin tip, oriented outward along its side. */
function buildPowerSymbol({ x, y, side, polarity }) {
  const dir =
    side === "left"
      ? { x: -1, y: 0 }
      : side === "right"
        ? { x: 1, y: 0 }
        : side === "top"
          ? { x: 0, y: -1 }
          : { x: 0, y: 1 };
  const perp = { x: -dir.y, y: dir.x };
  const g = svgEl("g", {
    class: `schematic-power schematic-power--${polarity}`,
  });
  const stemEnd = { x: x + dir.x * 0.9, y: y + dir.y * 0.9 };
  g.append(
    svgEl("line", {
      class: "schematic-power-stem",
      x1: x,
      y1: y,
      x2: stemEnd.x,
      y2: stemEnd.y,
    }),
  );
  const bar = (at, half) =>
    svgEl("line", {
      class: "schematic-power-bar",
      x1: at.x - perp.x * half,
      y1: at.y - perp.y * half,
      x2: at.x + perp.x * half,
      y2: at.y + perp.y * half,
    });
  if (polarity === "VCC") {
    g.append(bar(stemEnd, 0.8)); // one wide bar
  } else {
    // Ground: three shrinking rungs.
    [0.8, 0.5, 0.24].forEach((half, i) => {
      const at = {
        x: stemEnd.x + dir.x * i * 0.3,
        y: stemEnd.y + dir.y * i * 0.3,
      };
      g.append(bar(at, half));
    });
  }
  g.append(
    svgText(
      polarity,
      x + dir.x * 2.1,
      y + dir.y * 2.1,
      "schematic-power-label",
      0.85,
    ),
  );
  return g;
}

// ── The whole diagram ─────────────────────────────────────────────────────────

/** Build one polyline per routed segment of an edge. */
function buildEdge(edge) {
  const cls =
    "schematic-edge" +
    (edge.bus ? " schematic-edge--bus" : "") +
    (edge.dangling ? " schematic-edge--dangling" : "");
  const g = svgEl("g", {
    class: cls,
    "data-nets": (edge.netIds ?? []).join(" "),
  });
  for (const seg of edge.segments) {
    g.append(
      svgEl("polyline", {
        class: "schematic-wire",
        points: seg.map((p) => `${p.x},${p.y}`).join(" "),
      }),
    );
  }
  const text = edge.label?.text ?? edge.name;
  if (edge.label && text) {
    g.append(
      svgText(
        text,
        edge.label.x,
        edge.label.y,
        "schematic-net-label",
        edge.bus ? 1.1 : 0.95,
        edge.label.anchor ?? "start",
      ),
    );
  }
  return g;
}

// ── Distinctive-shape parts (LED / resistor / switch / button / PSU / clock) ──

/** The four transistor symbols (catalog/symbols.js). */
const TRANSISTOR_SHAPES = new Set(["npn", "pnp", "nmos", "pmos"]);

/** The small value text for a shape symbol (volts / ohms / Hz), or "". A
    semiconductor is named by its part number; a passive by its value. */
function shapeText(shape, params) {
  if (shape === "inductor") {
    const v = formatHenries(Number(params?.henries));
    return v ? `${v}H` : (partNumberOf(params) ?? "");
  }
  if (shape === "zener") {
    const v = formatVolts(Number(params?.zenerVolts));
    return partNumberOf(params) ?? (v ? `${v}V` : "");
  }
  if (shape === "diode" || shape === "bridge" || TRANSISTOR_SHAPES.has(shape)) {
    return partNumberOf(params) ?? "";
  }
  if (shape === "resistor" || shape === "potentiometer") {
    return formatOhms(Number(params?.ohms));
  }
  if (shape === "capacitor" || shape === "capacitor-polarized") {
    const v = formatFarads(Number(params?.farads));
    return v ? `${v}F` : "";
  }
  if (shape === "psu") return `${params?.volts ?? 5}V`;
  if (shape === "load") return loadBadge(params);
  if (shape === "ac") return acBadge(params).volts;
  if (shape === "transformer") return transformerBadge(params);
  if (shape === "adapter") return adapterBadge(params).volts;
  if (shape === "speaker") return `${speakerParams(params).impedance}Ω`;
  if (shape === "clock") {
    return params?.hz === "manual" ? "man" : `${params?.hz ?? 1}Hz`;
  }
  return "";
}

/** A filled arrowhead with its tip at (x, y), pointing along (dx, dy). */
function arrowhead(x, y, dx, dy, size = 0.42) {
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const bx = x - ux * size;
  const by = y - uy * size;
  const px = -uy * size * 0.5;
  const py = ux * size * 0.5;
  return svgEl("path", {
    class: "schematic-shape-arrow",
    d: `M ${x} ${y} L ${bx + px} ${by + py} L ${bx - px} ${by - py} Z`,
  });
}

/**
 * A transistor's symbol into `g`: the control in from the left at mid-height,
 * the switched pins leaving at `tx` (where catalog/symbols.js puts their
 * terminals) out of the top and bottom edges, all in a circle. A BJT is the
 * base bar with its collector and emitter diagonals, the arrow on the emitter
 * — out for an NPN, in for a PNP. A MOSFET is the gate plate beside a broken
 * (enhancement) channel, its body tied to the source, the arrow on that tie —
 * in for an N-channel, out for a P-channel.
 */
function buildTransistorShape(g, shape, w, h, line) {
  const cy = h / 2;
  const tx = w / 2 + 0.8;
  g.append(
    svgEl("circle", {
      class: "schematic-shape-line",
      cx: w / 2 + 0.15,
      cy,
      r: 1.55,
    }),
  );
  if (shape === "npn" || shape === "pnp") {
    const bx = 1.5;
    const inner = { top: cy - 0.4, bottom: cy + 0.4 };
    const outer = { top: cy - 1.15, bottom: cy + 1.15 };
    g.append(
      line(0, cy, bx, cy),
      line(bx, cy - 0.85, bx, cy + 0.85),
      line(bx, inner.top, tx, outer.top),
      line(tx, outer.top, tx, 0),
      line(bx, inner.bottom, tx, outer.bottom),
      line(tx, outer.bottom, tx, h),
    );
    const dx = tx - bx;
    if (shape === "npn") {
      // The emitter is the lower diagonal, and its arrow points OUT.
      const dy = outer.bottom - inner.bottom;
      g.append(arrowhead(bx + dx * 0.85, inner.bottom + dy * 0.85, dx, dy));
    } else {
      // The emitter is the upper one, and its arrow points IN.
      const dy = outer.top - inner.top;
      g.append(arrowhead(bx + dx * 0.35, inner.top + dy * 0.35, -dx, -dy));
    }
    return;
  }
  const gx = 1.25;
  const chx = 1.6;
  const lead = 0.72;
  // The source is the bottom pin of an N-channel and the top of a P-channel.
  const source = shape === "nmos" ? cy + lead : cy - lead;
  g.append(
    line(0, cy, gx, cy),
    line(gx, cy - 0.85, gx, cy + 0.85),
    line(chx, cy - 0.95, chx, cy - 0.5),
    line(chx, cy - 0.22, chx, cy + 0.22),
    line(chx, cy + 0.5, chx, cy + 0.95),
    line(chx, cy - lead, tx, cy - lead),
    line(tx, cy - lead, tx, 0),
    line(chx, cy + lead, tx, cy + lead),
    line(tx, cy + lead, tx, h),
    line(chx, cy, tx, cy),
    line(tx, cy, tx, source),
    shape === "nmos"
      ? arrowhead(chx + 0.08, cy, -1, 0)
      : arrowhead(chx + 0.7, cy, 1, 0),
  );
}

/** Draw the distinctive body of a shape symbol into `g` (local coords). */
function buildShapeBody(g, shape, geo, node) {
  const w = geo.width;
  const h = geo.height;
  const cx = w / 2;
  const cy = h / 2;
  const line = (x1, y1, x2, y2, cls = "schematic-shape-line") =>
    svgEl("line", { class: cls, x1, y1, x2, y2 });

  if (shape === "led") {
    // Diode triangle (anode top → cathode bottom) + cathode bar, filled in the
    // LED's colour; two little emission arrows.
    const bw = w * 0.34;
    const top = h * 0.28;
    const apex = h * 0.6;
    const body = svgEl("path", {
      class: "schematic-led-body",
      d: `M ${cx - bw} ${top} L ${cx + bw} ${top} L ${cx} ${apex} Z`,
    });
    const color = node.params?.color ?? "red";
    body.style.setProperty("--led-color", `var(--color-wire-${color})`);
    g.append(body, line(cx - bw, apex, cx + bw, apex, "schematic-led-bar"));
    for (const dx of [0.3, 0.9]) {
      g.append(
        line(
          cx + bw * dx,
          top - 0.1,
          cx + bw * dx + 0.6,
          top - 0.8,
          "schematic-led-ray",
        ),
      );
    }
  } else if (shape === "resistor") {
    // Zigzag body between the two leads.
    const mid = cy;
    const a = h * 0.3;
    const x0 = w * 0.22;
    const x1 = w * 0.78;
    const step = (x1 - x0) / 6;
    let d = `M 0 ${mid} L ${x0} ${mid}`;
    for (let i = 0; i < 6; i++) {
      const yy = i % 2 === 0 ? mid - a : mid + a;
      d += ` L ${x0 + step * (i + 0.5)} ${yy}`;
    }
    d += ` L ${x1} ${mid} L ${w} ${mid}`;
    g.append(svgEl("path", { class: "schematic-shape-line", d }));
  } else if (shape === "potentiometer") {
    // The resistor's zigzag (pin 1 left, pin 3 right), and the wiper coming
    // down from its top stub to an arrowhead on the track.
    const a = 0.6;
    const x0 = w * 0.22;
    const x1 = w * 0.78;
    const step = (x1 - x0) / 6;
    let d = `M 0 ${cy} L ${x0} ${cy}`;
    for (let i = 0; i < 6; i++) {
      d += ` L ${x0 + step * (i + 0.5)} ${i % 2 === 0 ? cy - a : cy + a}`;
    }
    d += ` L ${x1} ${cy} L ${w} ${cy}`;
    const tip = cy - a - 0.08;
    g.append(
      svgEl("path", { class: "schematic-shape-line", d }),
      line(cx, 0, cx, tip - 0.3),
      svgEl("path", {
        class: "schematic-shape-arrow",
        d: `M ${cx - 0.3} ${tip - 0.45} L ${cx} ${tip} L ${cx + 0.3} ${tip - 0.45} Z`,
      }),
      svgText(
        shapeText(shape, node.params),
        cx,
        h * 0.95,
        "schematic-shape-value",
        0.8,
      ),
    );
  } else if (shape === "capacitor" || shape === "capacitor-polarized") {
    // Two plates across the leads; a polarised part's negative plate is the
    // curved one, with a + by the positive lead (pin 1, on the left).
    const gap = 0.32;
    const plate = h * 0.32;
    const mid = h * 0.42;
    g.append(line(0, mid, cx - gap, mid), line(cx + gap, mid, w, mid));
    g.append(line(cx - gap, mid - plate, cx - gap, mid + plate));
    if (shape === "capacitor") {
      g.append(line(cx + gap, mid - plate, cx + gap, mid + plate));
    } else {
      g.append(
        svgEl("path", {
          class: "schematic-shape-line",
          d:
            `M ${cx + gap + 0.3} ${mid - plate} ` +
            `Q ${cx + gap - 0.1} ${mid} ${cx + gap + 0.3} ${mid + plate}`,
        }),
        line(cx - gap - 0.75, mid - plate, cx - gap - 0.35, mid - plate),
        line(
          cx - gap - 0.55,
          mid - plate - 0.2,
          cx - gap - 0.55,
          mid - plate + 0.2,
        ),
      );
    }
    g.append(
      svgText(
        shapeText(shape, node.params),
        cx,
        h * 0.92,
        "schematic-shape-value",
        0.8,
      ),
    );
  } else if (shape === "diode" || shape === "zener") {
    // Anode on top, as the LED stands: the lead down into a triangle pointing
    // at the cathode bar — bent at both ends for a Zener — and its part
    // number (a Zener's voltage, without one) beside it.
    const bw = w * 0.3;
    const top = h * 0.3;
    const apex = h * 0.62;
    g.append(
      line(cx, 0, cx, top),
      line(cx, apex, cx, h),
      svgEl("path", {
        class: "schematic-shape-arrow",
        d: `M ${cx - bw} ${top} L ${cx + bw} ${top} L ${cx} ${apex} Z`,
      }),
      shape === "zener"
        ? svgEl("path", {
            class: "schematic-shape-line",
            d:
              `M ${cx - bw - 0.3} ${apex + 0.3} L ${cx - bw} ${apex} ` +
              `L ${cx + bw} ${apex} L ${cx + bw + 0.3} ${apex - 0.3}`,
          })
        : line(cx - bw, apex, cx + bw, apex),
      svgText(
        shapeText(shape, node.params),
        cx + bw + 0.5,
        (top + apex) / 2,
        "schematic-shape-value",
        0.7,
        "start",
      ),
    );
  } else if (shape === "inductor") {
    // Four turns of coil between the leads, the value below.
    const x0 = w * 0.18;
    const x1 = w * 0.82;
    const turns = 4;
    const step = (x1 - x0) / turns;
    let d = `M 0 ${cy} L ${x0} ${cy}`;
    for (let i = 1; i <= turns; i++) {
      d += ` A ${step / 2} ${step / 2} 0 0 1 ${x0 + step * i} ${cy}`;
    }
    d += ` L ${w} ${cy}`;
    g.append(
      svgEl("path", { class: "schematic-shape-line", d }),
      svgText(
        shapeText(shape, node.params),
        cx,
        h * 0.92,
        "schematic-shape-value",
        0.8,
      ),
    );
  } else if (TRANSISTOR_SHAPES.has(shape)) {
    buildTransistorShape(g, shape, w, h, line);
    g.append(
      svgText(
        shapeText(shape, node.params),
        0.1,
        h - 0.3,
        "schematic-shape-value",
        0.7,
        "start",
      ),
    );
  } else if (shape === "switch") {
    // SPDT: a common pivot throwing a lever toward contact 1 (the upper pin).
    const pivot = { x: w * 0.32, y: cy };
    g.append(line(0, cy, pivot.x, pivot.y));
    g.append(line(pivot.x, pivot.y, w * 0.82, cy - 1));
    for (const dy of [-1, 1]) {
      g.append(
        svgEl("circle", {
          class: "schematic-shape-dot",
          cx: w * 0.85,
          cy: cy + dy,
          r: 0.28,
        }),
      );
    }
    g.append(
      svgEl("circle", {
        class: "schematic-shape-dot",
        cx: pivot.x,
        cy: pivot.y,
        r: 0.28,
      }),
    );
  } else if (shape === "button") {
    // Momentary: two contacts with a gap, a plunger bar above.
    g.append(line(0, cy, w * 0.36, cy), line(w * 0.64, cy, w, cy));
    for (const x of [w * 0.36, w * 0.64]) {
      g.append(
        svgEl("circle", { class: "schematic-shape-dot", cx: x, cy, r: 0.26 }),
      );
    }
    g.append(line(cx, cy, cx, h * 0.22));
    g.append(line(w * 0.32, h * 0.22, w * 0.68, h * 0.22));
  } else if (shape === "bridge") {
    // The diamond of four diodes, each pointing round toward + (the top
    // corner) from − (the bottom): left and right are the AC pins. Its part
    // number in the middle.
    const at = { l: [0, cy], t: [cx, 0], r: [w, cy], b: [cx, h] };
    g.append(
      svgEl("path", {
        class: "schematic-shape-line",
        d: `M 0 ${cy} L ${cx} 0 L ${w} ${cy} L ${cx} ${h} Z`,
      }),
    );
    const s = 0.42;
    for (const [[ax, ay], [kx, ky]] of [
      [at.l, at.t],
      [at.r, at.t],
      [at.b, at.l],
      [at.b, at.r],
    ]) {
      const len = Math.hypot(kx - ax, ky - ay);
      const [ux, uy] = [(kx - ax) / len, (ky - ay) / len];
      const [nx, ny] = [-uy, ux];
      const [mx, my] = [(ax + kx) / 2, (ay + ky) / 2];
      const [tipX, tipY] = [mx + s * ux, my + s * uy];
      g.append(
        svgEl("path", {
          class: "schematic-shape-arrow",
          d: `M ${mx - s * ux + s * nx} ${my - s * uy + s * ny} L ${mx - s * ux - s * nx} ${my - s * uy - s * ny} L ${tipX} ${tipY} Z`, // prettier-ignore
        }),
        line(tipX + s * nx, tipY + s * ny, tipX - s * nx, tipY - s * ny),
      );
    }
    g.append(
      svgText(shapeText(shape, node.params), cx, cy + 0.25, "schematic-shape-value", 0.6), // prettier-ignore
    );
  } else if (shape === "tact") {
    // The momentary button, each side's two legs joined by its strip.
    const strip = w * 0.18;
    for (const [x0, x1] of [
      [0, strip],
      [w, w - strip],
    ]) {
      g.append(
        line(x0, cy - 0.7, x1, cy - 0.7),
        line(x0, cy + 0.7, x1, cy + 0.7),
        line(x1, cy - 0.7, x1, cy + 0.7),
      );
    }
    g.append(line(strip, cy, w * 0.36, cy), line(w * 0.64, cy, w - strip, cy));
    for (const x of [w * 0.36, w * 0.64]) {
      g.append(
        svgEl("circle", { class: "schematic-shape-dot", cx: x, cy, r: 0.26 }),
      );
    }
    g.append(line(cx, cy, cx, h * 0.12));
    g.append(line(w * 0.32, h * 0.12, w * 0.68, h * 0.12));
  } else if (shape === "dpdt") {
    // Two SPDT poles (see "switch"), each lever on its A throw — the
    // released position — and the two tied by a dashed link. The throws sit
    // in from the edge, so each terminal's name reads on its own lead.
    const pivotX = w * 0.3;
    const throwX = w * 0.72;
    for (const yc of [cy - 1.8, cy + 1.8]) {
      g.append(line(0, yc, pivotX, yc));
      g.append(line(pivotX, yc, throwX - 0.3, yc - 0.9));
      for (const dy of [-1, 1]) {
        g.append(
          line(throwX, yc + dy, w, yc + dy),
          svgEl("circle", {
            class: "schematic-shape-dot",
            cx: throwX,
            cy: yc + dy,
            r: 0.28,
          }),
        );
      }
      g.append(
        svgEl("circle", {
          class: "schematic-shape-dot",
          cx: pivotX,
          cy: yc,
          r: 0.28,
        }),
      );
    }
    const linkX = (pivotX + throwX - 0.3) / 2;
    g.append(line(linkX, cy - 2.25, linkX, cy + 1.35, "schematic-shape-link"));
  } else if (shape === "speaker") {
    // A speaker as the textbook draws it: the magnet's box, its leads into
    // the top and bottom, the cone flaring out to the right — its impedance
    // beside the box.
    const bw = w * 0.2;
    const top = h * 0.32;
    const bottom = h * 0.68;
    const flare = h * 0.16;
    const right = cx + bw / 2;
    g.append(
      line(cx, 0, cx, top),
      line(cx, bottom, cx, h),
      svgEl("rect", { class: "schematic-shape-body", x: cx - bw / 2, y: top, width: bw, height: bottom - top }), // prettier-ignore
      svgEl("path", {
        class: "schematic-shape-line",
        d: `M ${right} ${top} L ${w * 0.95} ${top - flare} L ${w * 0.95} ${bottom + flare} L ${right} ${bottom}`, // prettier-ignore
      }),
      svgText(shapeText(shape, node.params), cx - bw / 2 - 0.2, cy, "schematic-shape-value", 0.7, "end"), // prettier-ignore
    );
  } else if (shape === "load") {
    // A current sink: a box with the arrow its current takes, + to −.
    g.append(
      svgEl("rect", { class: "schematic-shape-body", x: w * 0.15, y: h * 0.12, width: w * 0.7, height: h * 0.56, rx: 0.3 }), // prettier-ignore
      line(cx + w * 0.22, h * 0.2, cx + w * 0.22, h * 0.58),
      arrowhead(cx + w * 0.22, h * 0.6, 0, 1, 0.36),
      svgText(node.symbol.label, cx - w * 0.06, h * 0.42, "schematic-shape-label", 1), // prettier-ignore
      svgText(shapeText(shape, node.params), cx, h * 0.86, "schematic-shape-value", 0.85), // prettier-ignore
    );
  } else if (shape === "ac" || shape === "adapter") {
    // An AC source: the circle with a sine in it, its output beside. An AC
    // adapter is one too — its mains side is in the wall.
    const r = Math.min(w, h) * 0.34;
    let d = "";
    for (let k = 0; k <= 16; k++) {
      const x = cx - r * 0.7 + (1.4 * r * k) / 16;
      const y = cy - r * 0.4 * Math.sin((2 * Math.PI * k) / 16);
      d += `${k ? " L" : "M"} ${x.toFixed(3)} ${y.toFixed(3)}`;
    }
    g.append(
      line(cx, 0, cx, cy - r),
      line(cx, cy + r, cx, h),
      svgEl("circle", { class: "schematic-shape-body", cx, cy, r }),
      svgEl("path", { class: "schematic-shape-line", d }),
      svgText(shapeText(shape, node.params), cx + r + 0.3, cy, "schematic-shape-value", 0.75, "start"), // prettier-ignore
    );
  } else if (shape === "transformer") {
    // Two windings face to face across the core's two lines, a dot at each
    // winding's 1 end, its rating under them — inside the box, where the
    // layout keeps room for it.
    const top = h / 2 - 1.4;
    const bottom = h / 2 + 1.4;
    const coil = (x, dir) => {
      const loop = (bottom - top) / 4;
      let d = `M ${x} ${top}`;
      for (let k = 0; k < 4; k++) {
        const y = top + k * loop;
        d += ` C ${x + dir * 0.6} ${y} ${x + dir * 0.6} ${y + loop} ${x} ${y + loop}`; // prettier-ignore
      }
      return d;
    };
    const left = cx - 0.75;
    const right = cx + 0.75;
    g.append(
      line(0, top, left, top),
      line(0, bottom, left, bottom),
      line(right, top, w, top),
      line(right, bottom, w, bottom),
      svgEl("path", { class: "schematic-shape-line", d: coil(left, -1) }),
      svgEl("path", { class: "schematic-shape-line", d: coil(right, 1) }),
      line(cx - 0.15, top, cx - 0.15, bottom),
      line(cx + 0.15, top, cx + 0.15, bottom),
      svgEl("circle", { class: "schematic-shape-dot", cx: left - 0.45, cy: top - 0.35, r: 0.14 }), // prettier-ignore
      svgEl("circle", { class: "schematic-shape-dot", cx: right + 0.45, cy: top - 0.35, r: 0.14 }), // prettier-ignore
      svgText(shapeText(shape, node.params), cx, h - 0.35, "schematic-shape-value", 0.7), // prettier-ignore
    );
  } else if (shape === "psu" || shape === "clock") {
    // A source: circle (PSU) or square-wave box (clock).
    if (shape === "psu") {
      g.append(
        svgEl("circle", {
          class: "schematic-shape-body",
          cx,
          cy,
          r: Math.min(w, h) * 0.34,
        }),
      );
    } else {
      g.append(
        svgEl("rect", {
          class: "schematic-shape-body",
          x: w * 0.15,
          y: h * 0.15,
          width: w * 0.7,
          height: h * 0.5,
          rx: 0.3,
        }),
      );
      const y0 = h * 0.28;
      const y1 = h * 0.52;
      g.append(
        svgEl("path", {
          class: "schematic-shape-line",
          d: `M ${w * 0.24} ${y1} V ${y0} H ${w * 0.44} V ${y1} H ${w * 0.62} V ${y0} H ${w * 0.78}`,
        }),
      );
    }
    g.append(
      svgText(node.symbol.label, cx, cy, "schematic-shape-label", 1),
      svgText(
        shapeText(shape, node.params),
        cx,
        h * 0.86,
        "schematic-shape-value",
        0.85,
      ),
    );
  }
  // Resistor value sits below its zigzag; LED needs no text.
  if (shape === "resistor") {
    g.append(
      svgText(
        shapeText(shape, node.params),
        cx,
        h * 0.9,
        "schematic-shape-value",
        0.8,
      ),
    );
  }
}

/** Build one symbol node: its body (box or distinctive shape) + labelled stubs. */
function buildNode(node) {
  const geo = node.geometry;
  const shape = node.symbol.kind === "shape" ? node.symbol.shape : null;
  const g = svgEl("g", {
    class: `schematic-node${shape ? ` schematic-shape--${shape}` : ""}`,
    "data-id": node.id,
    "data-ref": node.ref,
    transform: `translate(${node.x} ${node.y})`,
  });

  if (shape) {
    buildShapeBody(g, shape, geo, node);
    if (shape === "led") {
      // Carry the anode/cathode nets so the sim can light the LED (anode H,
      // cathode L) exactly as the breadboard does.
      const a = node.portNets?.["pin:1"]?.[0];
      const k = node.portNets?.["pin:2"]?.[0];
      if (a) g.setAttribute("data-anode", a);
      if (k) g.setAttribute("data-cathode", k);
    }
  } else {
    g.append(
      svgEl("rect", {
        class: "schematic-box",
        x: 0,
        y: 0,
        width: geo.width,
        height: geo.height,
        rx: 0.4,
      }),
    );
    if (node.symbol.glyph) g.append(buildGlyph(node.symbol.glyph, geo));
    if (node.symbol.label) {
      g.append(
        svgText(
          node.symbol.label,
          geo.width / 2,
          geo.height / 2,
          "schematic-label",
          1.3,
        ),
      );
    }
  }

  for (const port of geo.ports) {
    const nets = (node.portNets?.[port.key] ?? []).join(" ");
    g.append(
      svgEl("line", {
        class: "schematic-stub",
        "data-nets": nets,
        x1: port.ex,
        y1: port.ey,
        x2: port.tx,
        y2: port.ty,
      }),
    );
    if (shape) {
      // A shape terminal is labelled just inside its edge (small).
      const lx =
        port.ex +
        (port.side === "left" ? 0.4 : port.side === "right" ? -0.4 : 0);
      const ly =
        port.ey +
        (port.side === "top" ? 0.7 : port.side === "bottom" ? -0.7 : 0);
      const anchor =
        port.side === "left"
          ? "start"
          : port.side === "right"
            ? "end"
            : "middle";
      g.append(
        svgText(port.name, lx, ly, "schematic-terminal-label", 0.7, anchor),
      );
      continue;
    }
    // A box pin is labelled inside on its side; power pins (top/bottom) rely on
    // their VCC/GND rail symbol instead.
    if (port.side === "top" || port.side === "bottom") continue;
    const lx = port.ex + (port.side === "left" ? 0.5 : -0.5);
    const anchor = port.side === "left" ? "start" : "end";
    g.append(
      svgText(port.name, lx, port.ey, "schematic-pin-label", 0.85, anchor),
    );
  }
  return g;
}

/**
 * Build the complete schematic SVG for a `layout()` result. Positioned in world
 * px (a child of the camera-transformed surface) with an inner viewBox in pitch
 * units so drawing coordinates are the layout's own. Pure DOM.
 */
export function buildSchematicSvg(result) {
  const b = result.bounds;
  const x0 = b.minX - PAD;
  const y0 = b.minY - PAD;
  const w = Math.max(1, b.maxX - b.minX + 2 * PAD);
  const h = Math.max(1, b.maxY - b.minY + 2 * PAD);
  const svg = svgEl("svg", {
    class: "schematic-svg",
    viewBox: `${x0} ${y0} ${w} ${h}`,
    width: w * PX_PER_UNIT,
    height: h * PX_PER_UNIT,
    style: `left:${x0 * PX_PER_UNIT}px; top:${y0 * PX_PER_UNIT}px;`,
  });

  const gEdges = svgEl("g", { class: "schematic-edges" });
  const gPower = svgEl("g", { class: "schematic-powers" });
  const gSignals = svgEl("g", { class: "schematic-signals" });
  const gNodes = svgEl("g", { class: "schematic-nodes" });
  for (const edge of result.edges) gEdges.append(buildEdge(edge));
  for (const stub of result.powerStubs) gPower.append(buildPowerSymbol(stub));
  for (const stub of result.signalStubs ?? []) {
    gSignals.append(buildSignalSymbol(stub));
  }
  for (const node of result.nodes) gNodes.append(buildNode(node));
  svg.append(gEdges, gPower, gSignals, gNodes);
  return svg;
}

/** The combined level of a net-id list (all-equal → that level; mixed → X). */
function combinedLevel(netIds, levels) {
  const first = levels.get(netIds[0]) ?? "Z";
  for (let i = 1; i < netIds.length; i++) {
    if ((levels.get(netIds[i]) ?? "Z") !== first) return "X";
  }
  return first;
}

const STATUSES = [
  "unpowered",
  "underpowered",
  "reversed",
  "damaged",
  "overloaded",
];

// The overlays' view of a built diagram, read ONCE per svg (a render builds a
// fresh one, so the index can never go stale): every net-carrying element with
// its parsed net ids, every LED and every symbol node, each with the value the
// overlay last WROTE. A tick then touches only what changed — every tick
// publishes, and rewriting ~2k unchanged attributes (re-querying and
// re-splitting them first) was the schematic's main-thread cost while running.
const overlayIndexes = new WeakMap();

function overlayIndex(svg) {
  let index = overlayIndexes.get(svg);
  if (index) return index;
  index = {
    nets: [...svg.querySelectorAll("[data-nets]")].map((el) => ({
      el,
      ids: el.getAttribute("data-nets").split(" ").filter(Boolean),
      level: el.dataset.level,
      lit: el.classList.contains("schematic--highlight"),
    })),
    leds: [...svg.querySelectorAll(".schematic-shape--led")].map((el) => ({
      el,
      anode: el.getAttribute("data-anode"),
      cathode: el.getAttribute("data-cathode"),
      lit: el.classList.contains("schematic-lit"),
    })),
    nodes: [...svg.querySelectorAll(".schematic-node")].map((el) => ({
      el,
      id: el.dataset.id,
      status:
        STATUSES.find((s) => el.classList.contains(`schematic-node--${s}`)) ??
        null,
    })),
  };
  overlayIndexes.set(svg, index);
  return index;
}

/** Tint every edge/stub by its net level while running (cleared otherwise),
    and light each LED (anode HIGH + cathode LOW) exactly as the breadboard.
    Writes only what changed since the last call on this svg. */
export function applyLevels(svg, levels = new Map(), running = false) {
  const index = overlayIndex(svg);
  for (const entry of index.nets) {
    const level =
      running && entry.ids.length
        ? combinedLevel(entry.ids, levels)
        : undefined;
    if (level === entry.level) continue;
    entry.level = level;
    if (level === undefined) delete entry.el.dataset.level;
    else entry.el.dataset.level = level;
  }
  for (const led of index.leds) {
    const lit =
      running &&
      levels.get(led.anode) === "H" &&
      levels.get(led.cathode) === "L";
    if (lit === led.lit) continue;
    led.lit = lit;
    led.el.classList.toggle("schematic-lit", lit);
  }
}

/** Reflect each chip's health as a status class on its symbol node. */
export function applyStatus(svg, chipStatus = new Map(), running = false) {
  for (const node of overlayIndex(svg).nodes) {
    const found = running ? chipStatus.get(node.id)?.status : null;
    const status = STATUSES.includes(found) ? found : null;
    if (status === node.status) continue;
    if (node.status) node.el.classList.remove(`schematic-node--${node.status}`);
    if (status) node.el.classList.add(`schematic-node--${status}`);
    node.status = status;
  }
}

/** Highlight (or clear) every element carrying `netId`, matching the probe. */
export function applyHighlight(svg, netId) {
  for (const entry of overlayIndex(svg).nets) {
    const on = Boolean(netId) && entry.ids.includes(netId);
    if (on === entry.lit) continue;
    entry.lit = on;
    entry.el.classList.toggle("schematic--highlight", on);
  }
}

export class SchematicView {
  #viewport;
  #deskView;
  #zoomControl;
  #doc;
  #netlist;
  #onSetSchematicPos;
  #onAutoLayout;

  #svg = null;
  #result = null;
  // The document changed while the diagram was hidden: it is laid out and
  // drawn again when it is next shown, not on every edit nobody can see
  // (a hidden schematic was most of an edit's cost on a big desk).
  #stale = true;
  #hint; // "add chips" overlay

  #levels = new Map();
  #chipStatus = new Map();
  #running = false;
  #highlightNet = null;

  #dragHint = null; // { id, x, y } — live, uncommitted
  #drag = null;
  #fitted = false;

  #onDocChanged;
  #onSimState;
  #onProbed;

  /**
   * @param {HTMLElement} viewport - the `.schematic-viewport` element to own.
   * @param {object} opts
   * @param {import('../model/desk-doc.js').DeskDoc} opts.doc
   * @param {(id:string,x:number,y:number)=>void} opts.onSetSchematicPos
   * @param {()=>void} [opts.onAutoLayout] - clear every position nudge.
   * @param {object} [opts.netlist] - injectable NetlistCache (tests).
   */
  constructor(
    viewport,
    { doc, onSetSchematicPos, onAutoLayout, netlist } = {},
  ) {
    this.#viewport = viewport;
    this.#doc = doc;
    this.#onSetSchematicPos = onSetSchematicPos;
    this.#onAutoLayout = onAutoLayout;
    this.#netlist = netlist ?? new NetlistCache(doc);

    this.#deskView = new DeskView(viewport, {
      camera: { cx: 0, cy: 0, zoom: 0.9 },
      onViewportChange: (camera) => this.#zoomControl?.setZoom(camera.zoom),
    });
    this.#deskView.surface.classList.add("schematic-surface");

    // The same bottom-right zoom cluster the desk carries: this view keeps its
    // OWN camera, so it keeps its own readout of it (the callback above fires
    // for the wheel, the ⌥⌘ keys and fit() alike). Owned here, not by app.js,
    // because the DeskView it mirrors is owned here.
    this.#zoomControl = new ZoomControl(viewport, {
      onZoomIn: (opts) => this.#deskView.zoomIn(opts),
      onZoomOut: (opts) => this.#deskView.zoomOut(opts),
      onReset: () => this.#deskView.resetZoom(),
    });
    this.#zoomControl.setZoom(this.#deskView.camera.zoom);

    this.#hint = el("p", {
      class: "schematic-hint",
      text: t("schematic.hint"),
      hidden: true,
    });
    viewport.append(this.#hint);
    if (onAutoLayout) {
      viewport.append(
        el("div", { class: "schematic-tools" }, [
          el("button", {
            class: "schematic-tool-btn",
            type: "button",
            text: t("schematic.autoLayout"),
            onClick: () => this.#onAutoLayout?.(),
          }),
        ]),
      );
    }

    this.#onDocChanged = (e) => {
      // A drag's own commit lands after it has torn down. A document REPLACED
      // under one (undo/redo, a desktop switch) may no longer hold its
      // symbol: let it go. Any other change mid-drag (a chip the run burnt, a
      // switch flipped) leaves the symbol where it is — the drag's end
      // redraws, by its commit or by its own render.
      if (e?.detail?.replaced) this.#cancelDrag();
      if (!this.#drag) this.#renderIfShown();
    };
    window.addEventListener("chiphippo:doc-changed", this.#onDocChanged);
    this.#onSimState = (e) => this.#applySim(e.detail);
    window.addEventListener("chiphippo:sim-state", this.#onSimState);
    this.#onProbed = (e) => this.#applyProbe(e.detail);
    window.addEventListener("chiphippo:net-probed", this.#onProbed);

    this.#deskView.surface.addEventListener("pointerdown", this.#onPointerDown);
    this.#renderIfShown();
  }

  /**
   * Re-render in the new language (see app.js's `relabelChrome`). The diagram
   * itself is redrawn from the document, and its symbol captions come from the
   * catalog through `partTitle()`, so a repaint is most of the job.
   */
  relocalize() {
    this.#hint.textContent = t("schematic.hint");
    const auto = this.#hint.parentElement?.querySelector(".schematic-tool-btn");
    if (auto) auto.textContent = t("schematic.autoLayout");
    this.#zoomControl.relocalize();
    this.#renderIfShown();
  }

  /** Show or hide the schematic; fit the diagram the first time it is shown.
      Shown, it is painted with the board as the last tick left it — a hidden
      schematic only remembers the live levels (`#applySim`). */
  setVisible(on) {
    this.#viewport.hidden = !on;
    if (on && this.#stale) this.#render();
    if (on && this.#svg) {
      applyLevels(this.#svg, this.#levels, this.#running);
      applyStatus(this.#svg, this.#chipStatus, this.#running);
      applyHighlight(this.#svg, this.#highlightNet);
    }
    if (on && !this.#fitted && this.#result?.nodes.length) {
      this.fit();
      this.#fitted = true;
    }
  }

  zoomIn(opts) {
    this.#deskView.zoomIn(opts);
  }

  zoomOut(opts) {
    this.#deskView.zoomOut(opts);
  }

  resetZoom() {
    this.#deskView.resetZoom();
  }

  zoomOutFull() {
    this.#deskView.zoomOutFull();
  }

  /** Space is down (or up): a left-drag anywhere pans — DeskView.setSpaceHeld. */
  setSpaceHeld(on) {
    this.#deskView.setSpaceHeld(on);
  }

  /** Whether a Space-drag pan used the hold just ended (and forget it). */
  takeSpacePanned() {
    return this.#deskView.takeSpacePanned();
  }

  /** Centre + scale the camera to frame the whole diagram. */
  fit() {
    if (this.#stale) this.#render();
    const b = this.#result?.bounds;
    if (!b) return;
    const rect = this.#viewport.getBoundingClientRect();
    const wPitch = b.maxX - b.minX + 2 * PAD;
    const hPitch = b.maxY - b.minY + 2 * PAD;
    let zoom = 0.9;
    if (rect.width && rect.height && wPitch && hPitch) {
      const zx = rect.width / (wPitch * PX_PER_UNIT);
      const zy = rect.height / (hPitch * PX_PER_UNIT);
      zoom = clampZoom(Math.min(zx, zy, 1.5));
    }
    this.#deskView.setCamera({
      cx: (b.minX + b.maxX) / 2,
      cy: (b.minY + b.maxY) / 2,
      zoom,
    });
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  #posHints() {
    const hints = {};
    for (const c of this.#doc.toJSON().components) {
      if (c.schematicPos) hints[c.id] = c.schematicPos;
    }
    if (this.#dragHint) {
      hints[this.#dragHint.id] = { x: this.#dragHint.x, y: this.#dragHint.y };
    }
    return hints;
  }

  /** Draw now if the diagram is on screen, else at the next show. */
  #renderIfShown() {
    if (this.#viewport.hidden) this.#stale = true;
    else this.#render();
  }

  #render() {
    this.#stale = false;
    this.#result = layout(
      this.#doc.toJSON(),
      this.#netlist.get(),
      this.#posHints(),
    );
    const svg = buildSchematicSvg(this.#result);
    if (this.#svg) this.#svg.replaceWith(svg);
    else this.#deskView.surface.append(svg);
    this.#svg = svg;
    this.#hint.hidden = this.#result.nodes.length > 0;

    // Re-apply the live overlays onto the freshly built DOM.
    applyLevels(svg, this.#levels, this.#running);
    applyStatus(svg, this.#chipStatus, this.#running);
    applyHighlight(svg, this.#highlightNet);
  }

  #applySim(detail) {
    this.#running = Boolean(detail?.running);
    this.#levels = detail?.netLevels ?? new Map();
    this.#chipStatus = detail?.chipStatus ?? new Map();
    // Every tick publishes, and tinting the whole diagram cost a tenth of the
    // main thread on a busy desk while nobody could see it (make profile):
    // hidden, it only remembers the board, and `setVisible` paints it.
    if (!this.#svg || this.#viewport.hidden) return;
    applyLevels(this.#svg, this.#levels, this.#running);
    applyStatus(this.#svg, this.#chipStatus, this.#running);
  }

  #applyProbe(detail) {
    const net = detail?.netId ?? null;
    if (net === this.#highlightNet) return;
    this.#highlightNet = net;
    // Hidden, it only remembers the net: a render (at the next show) applies
    // it, and walking every [data-nets] node of a diagram nobody can see was
    // a probe sweep's cost.
    if (this.#svg && !this.#viewport.hidden) applyHighlight(this.#svg, net);
  }

  // ── Symbol drag (nudge → schematicPos) ───────────────────────────────────────

  #onPointerDown = (e) => {
    if (e.button !== 0 || this.#drag) return;
    const nodeEl = e.target.closest?.(".schematic-node");
    if (!nodeEl) return; // empty space → let DeskView pan
    const id = nodeEl.dataset.id;
    const node = this.#result?.nodes.find((n) => n.id === id);
    if (!node) return;
    e.stopPropagation(); // don't let the camera pan
    const world = this.#deskView.worldFromEvent(e);
    this.#drag = {
      id,
      pointerId: e.pointerId,
      offX: world.x - node.x,
      offY: world.y - node.y,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      last: world,
      // The desk's drag plumbing (pointer-gesture.js): the release reaches
      // us wherever it happens, and a yanked capture or a lost window focus
      // ends the drag rather than leaving a symbol stuck to a button-less
      // cursor. Captured on the STABLE viewport, so #render() (which
      // replaces the svg mid-drag) never drops the gesture.
      teardown: beginPointerGesture(this.#viewport, e.pointerId, {
        onMove: this.#onPointerMove,
        onEnd: this.#onPointerUp,
      }),
    };
  };

  #onPointerMove = (e) => {
    const d = this.#drag;
    if (!d || e.pointerId !== d.pointerId) return;
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 4) return;
      d.moved = true;
    }
    const world = this.#deskView.worldFromEvent(e);
    d.last = world;
    this.#dragHint = { id: d.id, x: world.x - d.offX, y: world.y - d.offY };
    this.#render(); // live reflow of the dragged symbol's edges only
  };

  #onPointerUp = (e) => {
    const d = this.#drag;
    if (!d || e.pointerId !== d.pointerId) return;
    d.teardown();
    this.#drag = null;
    this.#dragHint = null;
    if (d.moved && e.type === "pointerup") {
      // Commit at the RELEASE point (releaseWorld) → doc-changed → a clean
      // re-render from the stored nudge.
      const world = releaseWorld(this.#deskView, e, d.last);
      this.#onSetSchematicPos?.(d.id, world.x - d.offX, world.y - d.offY);
    } else {
      this.#render(); // a click, or an aborted drag: back where it was
    }
  };

  /** Abandon a drag in flight — the document under it was replaced (an
      undo/redo, a desktop switch), so the symbol it holds may be gone. */
  #cancelDrag() {
    if (!this.#drag) return;
    this.#onPointerUp({ type: "pointercancel", pointerId: this.#drag.pointerId }); // prettier-ignore
  }

  dispose() {
    this.#cancelDrag();
    window.removeEventListener("chiphippo:doc-changed", this.#onDocChanged);
    window.removeEventListener("chiphippo:sim-state", this.#onSimState);
    window.removeEventListener("chiphippo:net-probed", this.#onProbed);
    this.#deskView.surface.removeEventListener(
      "pointerdown",
      this.#onPointerDown,
    );
    this.#deskView.dispose();
  }
}
